<script setup lang="ts">

import type NewsItem from "@/types/NewsItem.ts";
import ModalTrigger from "@/components/Interface/Modal/ModalTrigger.vue";
import {useGameStore} from "@/stores/gameStore.ts";
import PositionIcon from '@/components/Elements/Images/Icons/PositionIcon.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import NewsItemBaseStructure from '@/components/Sections/News/Types/Base/NewsItemBaseStructure.vue'
import NewsItemBase from '@/components/Sections/News/Types/Base/NewsItemBase.vue'
import { computed } from 'vue'
import { CDN } from '@/models/cdn.ts'
import { useI18n } from 'vue-i18n'
import SunkenMetricDisplay from '@/components/Elements/Display/SunkenMetricDisplay.vue'
import StatusBadge from '@/components/Elements/Badges/StatusBadge.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'

const { t } = useI18n()
const gameStore = useGameStore();
const playerRelationStore = usePlayerRelationStore();

const props = defineProps<{
  newsItem: NewsItem,
  toast: boolean
}>();

const title = computed(() => {
  return props.newsItem.contentData.winnerPlayerId === gameStore.player?.id ? t('news.types.battleWon') : t('news.types.battleLost');
});

const attackerShipChange = computed(() => {
  return props.newsItem.contentData.attackerShipsEnd - props.newsItem.contentData.attackerShipsStart;
});

const defenderShipChange = computed(() => {
  return props.newsItem.contentData.defenderShipsEnd - props.newsItem.contentData.defenderShipsStart;
});

const isPlayerAttacker = computed(() => {
  return props.newsItem.contentData.attackerPlayerId === gameStore.player?.id;
});

const isWinner = computed(() => {
  return props.newsItem.contentData.winnerPlayerId === gameStore.player?.id;
});

const isPlayerDefender = computed(() => {
  return props.newsItem.contentData.defenderPlayerId === gameStore.player?.id;
});

</script>

<template>
  <NewsItemBase
    :toast="toast"
    :toast-image="playerRelationStore.getProfileImageByPlayerId(newsItem.contentData.opponentPlayerId)"
    :toast-text="title"
  >
    <ModalTrigger type="battleReport" :data="{'battleReportId': props.newsItem.contentData.battleReportId}">
      <NewsItemBaseStructure
        :newsItem="props.newsItem"
        :item-image="playerRelationStore.getProfileImageByPlayerId(newsItem.contentData.opponentPlayerId)"
        :header="title"
      >
        <div class="flex flex-col gap-2 items-center text-center h-full">
            <!-- Top row: avatars -->
            <div class="flex gap-3 text-sm w-full">
              <!-- Attacker avatar -->
              <div class="flex items-center justify-center w-full relative flex-1">
                <StatusBadge v-if="isPlayerAttacker" :variant="isWinner ? 'success' : 'danger'" class="absolute left-0 bottom-0">
                  {{ isWinner ? t('news.labels.victory') : t('news.labels.defeat') }}
                </StatusBadge>
                <StyledImage :src="playerRelationStore.getProfileImageByPlayerId(props.newsItem.contentData.attackerPlayerId)" size="sm"/>
              </div>
              <!-- Spacer for vs column -->
              <div class="w-8"></div>
              <!-- Defender avatar -->
              <div class="flex items-center justify-center w-full relative flex-1">
                <StyledImage :src="playerRelationStore.getProfileImageByPlayerId(props.newsItem.contentData.defenderPlayerId)" size="sm"/>
                <StatusBadge v-if="isPlayerDefender" :variant="isWinner ? 'success' : 'danger'" class="absolute right-0 bottom-0">
                  {{ isWinner ? t('news.labels.victory') : t('news.labels.defeat') }}
                </StatusBadge>
              </div>
            </div>

            <!-- Bottom row: stats with vs in middle -->
            <div class="flex gap-3 text-sm flex-1 w-full">
              <!-- Attacker stats -->
              <SunkenMetricDisplay variant="compact" class="flex-col gap-1 flex-1 w-full justify-center">
                <div v-if="attackerShipChange !== 0" class="flex items-center">
                  <img :src="CDN.icon('ship')" class="w-5 h-5 mr-2"/>
                  <span class="text-xs" :class="attackerShipChange < 0 ? (isPlayerAttacker ? 'text-red-500' : 'text-green-500') : ''">{{ attackerShipChange }}</span>
                </div>
                <span v-else class="text-metal-400 text-xs">{{ t('news.labels.noCasualties') }}</span>
              </SunkenMetricDisplay>

              <!-- Middle: vs @ location -->
              <div class="flex flex-col items-center justify-center text-metal-500 text-xs shrink-0">
                <span>{{ t('news.labels.vs') }}</span>
                <PositionIcon @click.stop :x="props.newsItem.contentData.xPos" :y="props.newsItem.contentData.yPos"/>
              </div>

              <!-- Defender stats -->
              <SunkenMetricDisplay variant="compact" class="flex-col flex-1 w-full !justify-evenly">
                <div v-if="defenderShipChange !== 0" class="flex items-center">
                  <img :src="CDN.icon('ship')" class="w-5 h-5 mr-2"/>
                  <span class="text-xs" :class="defenderShipChange < 0 ? (isPlayerDefender ? 'text-red-500' : 'text-green-500') : ''">{{ defenderShipChange }}</span>
                </div>
                <span v-else-if="props.newsItem.contentData.planetId === 0" class="text-metal-400 text-xs">{{ t('news.labels.noCasualties') }}</span>
                <!-- Defender planet -->
                <div v-if="props.newsItem.contentData.planetId > 0" class="flex items-center gap-2">
                  <img :src="CDN.planet(128, props.newsItem.contentData.planetSize, props.newsItem.contentData.planetType)" alt="Planet" class="w-5 h-5 rounded planet-shadow shrink-0"/>
                  <span class="text-xs hidden 2xl:inline">{{ props.newsItem.contentData.planetName }}</span>
                </div>
                <div v-if="props.newsItem.contentData.planetId > 0" class="flex items-center gap-1">
                  <img :src="CDN.icon('shield')" class="w-5 h-5 mr-2"/>
                  <span class="text-xs">{{ props.newsItem.contentData.defenderPlanetShieldsEnd }}%</span>
                  <img :src="CDN.icon('soldier')" class="w-5 h-5 mx-2"/>
                  <span class="text-xs">{{ props.newsItem.contentData.defenderPlanetTroopsEnd }}%</span>
                </div>
              </SunkenMetricDisplay>
            </div>
          </div>
      </NewsItemBaseStructure>
    </ModalTrigger>
  </NewsItemBase>
</template>

<style scoped lang="scss">
.planet-shadow {
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.6));
}
</style>
